import { Injectable, OnDestroy } from '@angular/core';
import * as L from 'leaflet';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { ApiService } from '../../../../services/api.service';
import { UtilsService } from '../../../../services/utils.service';
import { createNavigationGlobalIcon } from '../../../../shared/service/icon.service';
import { categoryColor } from '../shared/models/ais-vessel-types';
import {
  PlaybackAvailableType,
  PlaybackController,
  PlaybackState,
  EMPTY_PLAYBACK_STATE,
} from '../shared/contracts/playback-controller';
import { ClusterLayer } from '../shared/leaflet/cluster.layer';
import {
  AisstreamStep,
  AisstreamTimeline,
  availableTypes as computeAvailableTypes,
  buildTimeline,
  indexAtTime,
  interpolateAt,
} from './aisstream-playback.model';
import {
  buildAisstreamClusterPopup,
  buildAisstreamVesselPopup,
} from './aisstream-popup';

export type AisstreamMode = 'live' | 'playback' | 'idle';

// `AisAvailableType` was a deprecated alias for the shared contract's
// `PlaybackAvailableType`, kept so external callers could migrate on their own
// schedule. They have: nothing in `src/` names it any more, so the alias has
// finished its job. Import `PlaybackAvailableType` from
// `shared/contracts/playback-controller` directly.

/**
 * Component-scoped service (declared in providers[] of LeafletNewcollectionsComponent).
 * Angular creates and destroys one instance per component instance — no manual cleanup needed.
 *
 * Owns ALL AISstream-specific logic:
 *   - SSE EventSource lifecycle (live mode)
 *   - History HTTP fetch + step storage (playback mode)
 *   - Vessel marker rendering on the Leaflet map
 *
 * Component integration (3 lines only):
 *   1. @Component({ providers: [AisstreamPlaybackController] })
 *   2. constructor(public aisstreamLayer: AisstreamPlaybackController) {}
 *   3. this.aisstreamLayer.attach(this.map, taskId, collection) when an AISstream collection opens
 */
@Injectable()
export class AisstreamPlaybackController extends PlaybackController implements OnDestroy {
  private map:         L.Map | null = null;
  private vesselLayer: L.LayerGroup | null = null;
  private liveSource:  EventSource | null = null;
  private destroy$   = new Subject<void>();
  private aoiLayer:   L.GeoJSON | null = null;
  private aoiGeoJson: any = null;

  mode: AisstreamMode = 'idle';

  // ── PlaybackController state stream ──────────────────────────────────────────
  private _state$ = new BehaviorSubject<PlaybackState>({ ...EMPTY_PLAYBACK_STATE });
  readonly state$: Observable<PlaybackState> = this._state$.asObservable();
  /** ISO timestamp of the most recent data (live frame or loaded history). */
  private _lastUpdated: string | null = null;
  /** True while a history load is in flight. */
  private _loading = false;

  /** Steps fetched for the slider. Component reads this to populate sliderDateRangeArray. */
  steps: AisstreamStep[] = [];

  // ── Playback engine state ───────────────────────────────────────────────────
  /** Playback timer (single ~200ms tick drives the animation). */
  private playTimer: any = null;
  private static readonly TICK_MS = 200;

  /** Per-step lookups precomputed on load for fast interpolation (D14). */
  private _timeline: AisstreamTimeline = { times: [], maps: [] };
  private _virtualTime = 0;                          // current epoch ms on the timeline
  private _liveTaskId: string | null = null;         // remembered so X can resume live
  private _lastLiveVessels: any[] = [];              // last SSE frame — for live filtering

  isPlaying = false;
  speed = 60;                                        // simulated-seconds per real-second
  currentIndex = 0;
  showLabels = false;                                // vessel name labels off until toggled (T icon)
  /** Empty set = show all categories. */
  typeFilter = new Set<string>();

  /**
   * Group nearby vessels into count bubbles. ON by default, matching Voyage
   * Radar: an SSE feed over a busy strait draws the same unreadable texture a
   * bbox snapshot does, and the operator should not have to discover the fix.
   */
  private _clusterVessels = true;

  /**
   * The count-bubble pass — shared (`map-core/cluster.layer.ts`), not this
   * file's. Only the two hooks below are AISstream's.
   *
   * No `visible` hook: a hull that fails the type filter is REMOVED from the
   * layer here rather than dimmed, so every marker this controller holds is one
   * that should be drawn. No `hide` hook either — the layer's default (opacity
   * plus `display`) is right for these markers.
   */
  private readonly _cluster = new ClusterLayer(() => this.map, {
    // The analogue of Voyage Radar's "never absorb the SELECTED vessel". This
    // controller has no selection, but an open popup is the same statement:
    // swallowing that hull would leave its card pointing at empty water.
    absorbable: (marker: any) => !marker.isPopupOpen?.(),
    onIndivisible: (at: any, members: any[]) => this._openClusterList(at, members),
  });

  /**
   * Re-buckets on view changes (null while detached).
   *
   * NOT optional polish: the grid is anchored in world pixels at the current
   * zoom, so without this a bubble never splits as you zoom in — which is the
   * entire way out of one. Bound to zoom AND move because the pass only
   * considers markers within the padded viewport.
   */
  private _viewportRepaint: (() => void) | null = null;

  constructor(
    private apiService:   ApiService,
    private utilsService: UtilsService,
  ) {
    super();
    this._emit();
  }

  // ── PlaybackController glue ──────────────────────────────────────────────────

  snapshot(): PlaybackState {
    return this._state$.value;
  }

  /** Rebuild + publish the current state snapshot for the toolbar controls. */
  private _emit(): void {
    this._state$.next({
      mode: this.mode,
      isPlaying: this.isPlaying,
      currentIndex: this.currentIndex,
      stepCount: this.steps.length,
      stepLabels: this.stepLabels(),
      speed: this.speed,
      showLabels: this.showLabels,
      clusterVessels: this._clusterVessels,
      typeFilter: new Set(this.typeFilter),
      availableTypes: this.availableTypes(),
      lastUpdated: this._lastUpdated,
      loading: this._loading,
    });
  }

  /** Load history for a UTC range and enter playback (PlaybackController). */
  enterPlayback(fromIso: string, toIso: string): Promise<number> {
    const id = this._liveTaskId;
    if (!id) return Promise.resolve(0);
    return this.loadHistory(id, fromIso, toIso);
  }

  /**
   * Optional hook set by the host component so the (collection-specific) history
   * date picker stays where the runtime TOI window lives. The `history-entry` and
   * `date-range` toolbar controls invoke this via `requestHistory()`.
   */
  onRequestHistory: (() => void) | null = null;

  override requestHistory(): void {
    this.onRequestHistory?.();
  }

  /** Tear down the toolbar entirely (PlaybackController). */
  close(): void {
    this.detach();
  }

  // ── Lifecycle ──────────────────────────────────────────────────────────────

  /** Call when an AISstream collection is selected. */
  attach(map: L.Map, taskId: string, collection: any): void {
    this.detach(); // clean up any previous state
    this.map         = map;
    this.vesselLayer = L.layerGroup().addTo(map);
    this._viewportRepaint = () => this._applyClusters();
    map.on('zoomend moveend', this._viewportRepaint);
    this._drawAoi(collection);
    this._startLive(taskId);
  }

  /** Call when switching to a different collection or entering playback mode. */
  detach(): void {
    this._stopLive();
    this.pause();
    // Before the map reference goes: both need it to take their layers off.
    if (this._viewportRepaint && this.map) {
      this.map.off('zoomend moveend', this._viewportRepaint);
    }
    this._viewportRepaint = null;
    this._cluster.detach();
    this.vesselLayer?.clearLayers();
    this._markers.clear();
    if (this.vesselLayer && this.map) {
      this.map.removeLayer(this.vesselLayer);
    }
    if (this.aoiLayer && this.map) {
      try { this.map.removeLayer(this.aoiLayer); } catch (_) {}
    }
    this.vesselLayer = null;
    this.aoiLayer    = null;
    this.aoiGeoJson  = null;
    this.map         = null;
    this.steps       = [];
    this._timeline   = { times: [], maps: [] };
    this._virtualTime = 0;
    this.currentIndex = 0;
    this._lastLiveVessels = [];
    this.typeFilter  = new Set<string>();
    this.mode        = 'idle';
    this._lastUpdated = null;
    this._loading = false;
    this._emit();
  }

  /**
   * Re-attaches the target AOI polygon after resetMapNew() removes all non-tile layers.
   * No-op when no AOI is set.
   */
  ensureAoiLayerOnMap(): void {
    if (!this.aoiGeoJson || !this.map) return;
    try {
      if (this.aoiLayer) {
        if (!this.map.hasLayer(this.aoiLayer)) {
          this.aoiLayer.addTo(this.map);
        }
      } else {
        this.aoiLayer = this._createAoiGeoJson(this.aoiGeoJson).addTo(this.map);
      }
    } catch (_) {}
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.detach();
  }

  // ── Live mode ──────────────────────────────────────────────────────────────

  private _startLive(taskId: string): void {
    this._stopLive();
    this.mode        = 'live';
    this._liveTaskId = taskId;
    this.liveSource  = this.apiService.openAisstreamLive(taskId);
    this.liveSource.addEventListener('vessels', (e: MessageEvent) => {
      try {
        const vessels = JSON.parse(e.data);
        this._lastLiveVessels = vessels;
        this._renderVessels(vessels);
        this._lastUpdated = new Date().toISOString();
        this._emit();
      } catch { /* malformed frame — skip */ }
    });
    // EventSource reconnects automatically on onerror — no extra handling needed
    this._emit();
  }

  private _stopLive(): void {
    this.liveSource?.close();
    this.liveSource = null;
  }

  // ── Playback mode ──────────────────────────────────────────────────────────

  /** Fetch all steps for the given UTC range. Stops live stream while fetching. */
  loadHistory(taskId: string, fromIso: string, toIso: string): Promise<number> {
    this._stopLive();
    this.pause();
    this.mode = 'playback';
    this._liveTaskId = taskId;
    this._loading = true;
    this._emit();
    return new Promise((resolve, reject) => {
      this.apiService
        .getAisstreamHistory(taskId, fromIso, toIso)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (res) => {
            this.steps = res.steps ?? [];
            this._buildTimeline();
            this.currentIndex = 0;
            this._virtualTime = this._timeline.times[0] ?? 0;
            this._renderAtTime(this._virtualTime);
            this._lastUpdated = this.steps.length
              ? this.steps[this.steps.length - 1].time
              : this._lastUpdated;
            this._loading = false;
            this._emit();
            resolve(this.steps.length);
          },
          error: (err) => {
            // The toolbar swaps live→playback off `mode`, which was set
            // optimistically above. Without reverting it here a failed history
            // load strands the user in an empty playback bar with the live bar
            // gone, recoverable only by pressing X.
            this._loading = false;
            this.mode = 'live';
            this.steps = [];
            this._buildTimeline();
            this._emit();
            this.resumeLive(taskId);
            reject(err);
          },
        });
    });
  }

  /** Precompute per-step epoch times and mmsi→vessel lookups for interpolation. */
  private _buildTimeline(): void {
    this._timeline = buildTimeline(this.steps);
  }

  /** ISO/UTC label per step — drives the toolbar timestamp + slider. */
  stepLabels(): string[] {
    return this.steps.map((s) => s.time);
  }

  /** Distinct ship-type categories present in the current data (for the filter chips). */
  availableTypes(): PlaybackAvailableType[] {
    // Which lists to scan is a MODE question and stays here; the counting is
    // the model's. Live offers only what is on screen; playback offers every
    // loaded frame, so a hull that has left the view is still filterable.
    return computeAvailableTypes(
      this.mode === 'live' ? [this._lastLiveVessels] : this.steps.map((s) => s.vessels),
    );
  }

  // ── Playback transport ───────────────────────────────────────────────────────

  play(): void {
    if (this.isPlaying || this.steps.length < 2) return;
    // Restart from the beginning if parked at the end.
    if (this._virtualTime >= this._lastStepTime()) {
      this._virtualTime = this._timeline.times[0] ?? 0;
    }
    this.isPlaying = true;
    this.playTimer = setInterval(() => this._tick(), AisstreamPlaybackController.TICK_MS);
    this._emit();
  }

  pause(): void {
    this.isPlaying = false;
    if (this.playTimer) {
      clearInterval(this.playTimer);
      this.playTimer = null;
    }
    this._emit();
  }

  togglePlay(): void {
    this.isPlaying ? this.pause() : this.play();
  }

  /** Jump to a discrete step (slider drag / step buttons). */
  seek(index: number): void {
    const i = Math.max(0, Math.min(index, this.steps.length - 1));
    this.currentIndex = i;
    this._virtualTime = this._timeline.times[i] ?? 0;
    this._renderAtTime(this._virtualTime);
    this._emit();
  }

  stepForward(): void { this.seek(this.currentIndex + 1); }
  stepBack():    void { this.seek(this.currentIndex - 1); }
  skipToStart(): void { this.seek(0); }
  skipToEnd():   void { this.seek(this.steps.length - 1); }

  setSpeed(n: number): void { this.speed = n; this._emit(); }

  toggleLabels(): void {
    this.showLabels = !this.showLabels;
    // Branch on mode exactly as `setTypeFilter` below does. Rendering the
    // playback timeline while LIVE would draw the (empty or stale) step frame
    // over the streamed vessels — invisible for as long as this control only
    // rode the playback bar, and the first thing the live bar's T button would
    // have done.
    if (this.mode === 'live') {
      this._renderVessels(this._lastLiveVessels);
    } else {
      this._renderAtTime(this._virtualTime);
    }
    this._emit();
  }

  /**
   * Group nearby vessels into count bubbles, or draw every hull.
   *
   * Emits before acting, like Voyage Radar's, and does NOT re-render the frame
   * even in playback: clustering only decides which of the markers already on
   * screen are shown, so re-rendering would tear down and rebuild every marker
   * to answer a question about the ones already there.
   */
  override toggleClusters(): void {
    this._clusterVessels = !this._clusterVessels;
    this._emit();
    this._applyClusters();
  }

  setTypeFilter(set: Set<string>): void {
    this.typeFilter = new Set(set);
    if (this.mode === 'live') {
      this._renderVessels(this._lastLiveVessels);
    } else {
      this._renderAtTime(this._virtualTime);
    }
    this._emit();
  }

  /** Backwards-compatible single-step render (kept for any external callers). */
  renderStep(index: number): void {
    this.seek(index);
  }

  /** Resume live stream after returning from playback (X on the toolbar). */
  resumeLive(taskId?: string): void {
    this.pause();
    const id = taskId ?? this._liveTaskId;
    if (id && this.mode !== 'live') this._startLive(id);
  }

  private _lastStepTime(): number {
    const t = this._timeline.times;
    return t[t.length - 1] ?? 0;
  }

  private _tick(): void {
    // Advance virtual time by (real tick) × speed.
    this._virtualTime += AisstreamPlaybackController.TICK_MS * this.speed;
    const end = this._lastStepTime();
    if (this._virtualTime >= end) {
      this._virtualTime = end;
      this.currentIndex = this.steps.length - 1;
      this._renderAtTime(this._virtualTime);
      this.pause();
      return;
    }
    const prevIndex = this.currentIndex;
    this.currentIndex = indexAtTime(this._timeline.times, this._virtualTime);
    this._renderAtTime(this._virtualTime);
    // Render every tick (that is what makes vessels glide), but publish state
    // only when the visible step changes: _emit() rebuilds stepLabels and
    // rescans every step for availableTypes, which at 5 ticks/s over a long
    // history is a full O(steps × vessels) sweep several times a second.
    // The sibling controllers guard the same way.
    if (this.currentIndex !== prevIndex) this._emit();
  }

  /**
   * Draw the fleet at an arbitrary point on the timeline. The interpolation
   * that makes markers glide is the model's (`interpolateAt`); what stays here
   * is handing the result to Leaflet.
   */
  private _renderAtTime(vt: number): void {
    if (!this.steps.length) return;
    this._renderVessels(interpolateAt(this._timeline, vt));
  }

  // ── AOI drawing ────────────────────────────────────────────────────────────

  private _drawAoi(collection: any): void {
    if (!this.map) return;
    try {
      const rawTarget = collection?.target;
      if (!rawTarget) return;
      this.aoiGeoJson = typeof rawTarget === 'string' ? JSON.parse(rawTarget) : rawTarget;
      this.aoiLayer   = this._createAoiGeoJson(this.aoiGeoJson).addTo(this.map);
      const bounds = this.aoiLayer.getBounds();
      if (bounds?.isValid()) this.map.fitBounds(bounds, { padding: [50, 50] });
    } catch (_) {}
  }

  private _createAoiGeoJson(geoJson: any): L.GeoJSON {
    return L.geoJSON(geoJson, {
      style: () => ({
        color: 'yellow',
        fillColor: 'transparent',
        weight: 2,
        dashArray: '6 4',
      }),
      interactive: false,
    });
  }

  // ── Rendering ──────────────────────────────────────────────────────────────

  /** Cache of rendered marker icons keyed by category color (the icon depends only on color). */
  private _iconCache = new Map<string, L.Icon>();

  /** Live marker pool keyed by MMSI — reused across renders to avoid DOM churn. */
  private _markers = new Map<string, L.Marker>();

  /** Get (or lazily build + cache) the marker icon for a category color. */
  private _iconForColor(color: string): L.Icon {
    let icon = this._iconCache.get(color);
    if (!icon) {
      const iconUrl = createNavigationGlobalIcon(color).toDataURL();
      icon = L.icon({ iconUrl, iconSize: [28, 28], iconAnchor: [14, 14], popupAnchor: [0, -14] });
      this._iconCache.set(color, icon);
    }
    return icon;
  }

  private _renderVessels(vessels: any[]): void {
    if (!this.vesselLayer || !this.map) return;

    // Re-add the layer group if resetMapNew() removed it from the map
    if (!this.map.hasLayer(this.vesselLayer)) {
      this.vesselLayer.addTo(this.map);
    }

    // Reuse markers across renders (keyed by MMSI) instead of recreating them every
    // tick — updating position/icon is far cheaper than tearing down + rebuilding DOM.
    const seen = new Set<string>();

    for (const vessel of vessels) {
      // Shape from backend toVesselGeoJson():
      // { id, staticData: { mmsi, name, shipType }, shipTypeCategory,
      //   lastPositionUpdate: { latitude, longitude, course, speed, navStatusLabel } }
      const pos  = vessel.lastPositionUpdate ?? {};
      const stat = vessel.staticData ?? {};
      const lat  = pos.latitude;
      const lng  = pos.longitude;
      if (lat == null || lng == null) continue;

      // Client-side vessel-type filter (empty set = show all)
      const category = String(vessel.shipTypeCategory ?? 'OTHER');
      if (this.typeFilter.size && !this.typeFilter.has(category)) continue;

      const key   = String(stat.mmsi ?? vessel.id ?? `${lat},${lng}`);
      const color = categoryColor(category);
      seen.add(key);

      let marker = this._markers.get(key);
      if (!marker) {
        marker = L.marker([lat, lng], {
          icon: this._iconForColor(color),
          zIndexOffset: 100000,
          riseOnHover:  true,
        });
        // Build the popup HTML lazily from the marker's latest stashed data (only on open)
        marker.bindPopup(
          (m: any) => buildAisstreamVesselPopup(m._aisVessel, m._aisCategory, m._aisColor),
          { className: 'ais-vessel-popup', closeButton: false, offset: L.point(0, -6) },
        );
        this._markers.set(key, marker);
        this.vesselLayer.addLayer(marker);
      } else {
        marker.setLatLng([lat, lng]);
        if ((marker as any)._aisColor !== color) {
          marker.setIcon(this._iconForColor(color));
        }
      }

      // Stash latest data for the lazy popup + label
      (marker as any)._aisVessel   = vessel;
      (marker as any)._aisCategory = category;
      (marker as any)._aisColor    = color;

      // T-toggle: permanent name label when labels are on
      if (this.showLabels) {
        const name = String(stat.name ?? 'Unknown');
        if (marker.getTooltip()) {
          marker.setTooltipContent(name);
        } else {
          marker.bindTooltip(name, {
            permanent: true,
            direction: 'top',
            offset:    L.point(0, -14),
            className: 'ais-vessel-label',
          });
        }
      } else if (marker.getTooltip()) {
        marker.unbindTooltip();
      }
    }

    // Drop markers that are gone from this frame (or filtered out)
    this._markers.forEach((marker, key) => {
      if (!seen.has(key)) {
        this.vesselLayer!.removeLayer(marker);
        this._markers.delete(key);
      }
    });

    // LAST, and only here: the pass decides which of the markers above are
    // actually shown, so it has to see the finished pool. One call site covers
    // live frames, playback frames and both display toggles, because all four
    // routes end in this method.
    this._applyClusters();
  }

  /**
   * Run the count-bubble pass over the markers currently held.
   *
   * Called at the end of every render and on every view change — the two
   * moments that can change either what is drawn or how much room it has.
   */
  private _applyClusters(): void {
    this._cluster.apply(this._clusterVessels, Array.from(this._markers.values()));
  }

  /**
   * A bubble was clicked that zooming cannot open — hulls moored together, or a
   * map already at max zoom. Name them instead, so the click is never a no-op.
   *
   * Capped at twelve with a remainder line: the popup is an answer to "what is
   * under here", not a manifest, and an un-capped list of eighty would run off
   * the map.
   */
  private _openClusterList(at: any, members: any[]): void {
    if (!this.map) return;
    // Markers -> plain rows here, markup there: the builder never learns what a
    // Leaflet marker is, which is what keeps it testable.
    const rows = members.map((m: any) => {
      const stat = m._aisVessel?.staticData ?? {};
      return { name: stat.name, mmsi: stat.mmsi };
    });
    L.popup({ className: 'ais-vessel-popup', closeButton: false, offset: L.point(0, -6) })
      .setLatLng(at)
      .setContent(buildAisstreamClusterPopup(rows))
      .openOn(this.map);
  }

}
